import clsx from 'clsx'
import { computed, defineComponent, ref, Teleport } from 'vue'

import BaseModal from '@/components/base/modal/BaseModal'
import VButton from '@/components/common/button/VButton'
import VTextField from '@/components/common/text-field/VTextField'
import usePromptModal from '@/composables/common/modal/usePromptModal'

interface Props {
  tData?: string
  modalkey: symbol
  id?: string
  class?: string
  title: string
  content: string
  textFieldLabel?: string
  footerContent?: string
  cancelText?: string
  confirmText?: string
  confirmDisabled?: boolean
  confirmColor?: string
  cancelHidden?: boolean
  placeholder?: string
}

export default defineComponent((props: Props, context) => {
  const mainModal = usePromptModal<string>(props.modalkey)
  const message = ref('')

  // #region Props Getters

  const tData = computed(() => props.tData ?? 'prompt-modal')
  const confirmText = computed(() => props.confirmText ?? 'confirm')
  const cancelText = computed(() => props.cancelText ?? 'Cancel')

  // #endregion

  // #region Getters

  const cancelHidden = computed(() => props.cancelHidden ?? false)

  // #endregion

  return () => (
    <Teleport to='body'>
      <BaseModal
        t-data={tData.value}
        modalkey={mainModal.key}
        id={props.id}
        class={props.class}
      >
        <div class='mt-24 w-[368px] tablet:w-[564px]'>
          <form
            class='w-full rounded bg-white p-6 shadow-elevation-10'
            onSubmit={(e) => {
              e.preventDefault()
              mainModal.confirm(message.value)
            }}
          >
            <div class='flex space-x-2'>
              {context.slots.icon &&
                <div class='flex-none'>
                  {context.slots.icon?.()}
                </div>}
              <div>
                <h1
                  t-data={`${tData.value}-title`}
                  class='text-heading-6 break-word'
                >{props.title}
                </h1>
                <div
                  t-data={`${tData.value}-content`}
                  class='mt-4 whitespace-pre-line text-body-1 break-word'
                >{props.content}
                </div>
              </div>
            </div>
            <div class='mt-4 flex'>
              <VTextField
                t-data={`${props.tData}-text-field`}
                class='w-full'
                type='text'
                label={props.textFieldLabel}
                placeholder={props.placeholder}
                value={message.value}
                onInput={(value) => { message.value = value }}
              />
            </div>
            {props.footerContent &&
              <div
                t-data={`${tData.value}-footer-content`}
                class='mt-4 whitespace-pre-line text-body-1 break-word'
              >
                {props.footerContent}
              </div>}
            <div class='mt-4 flex justify-end space-x-4'>
              {!cancelHidden.value &&
                <VButton
                  t-data={`${tData.value}-cancel-btn`}
                  class='btn-medium min-w-22'
                  color={clsx('btn-gray')}
                  onClick={() => { mainModal.cancel() }}
                >
                  {cancelText.value}
                </VButton>}
              <VButton
                t-data={`${tData.value}-confirm-btn`}
                type='submit'
                class='btn-medium min-w-22'
                color={props.confirmColor}
                autofocus
                disabled={props.confirmDisabled}
              >
                {confirmText.value}
              </VButton>
            </div>
          </form>
        </div>
      </BaseModal>
    </Teleport>
  )
},
{
  name: 'PromptModal',
  props: [
    'tData',
    'modalkey',
    'id',
    'class',
    'title',
    'content',
    'textFieldLabel',
    'footerContent',
    'cancelText',
    'confirmText',
    'confirmDisabled',
    'confirmColor',
    'cancelHidden',
    'placeholder'
  ]
}
)
